import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import Papa, { ParseError, ParseResult } from "papaparse";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { toast } from "react-toastify";
import { PageContainer } from "../../components/PageContainer";
import {
  PREVIEW_HEADERS,
  buildBandImportPayload,
  parseBandCsvTableRows,
} from "./bandCsvImportParser";
import type { CsvBandRow } from "./bandCsvImportParser";
import { IMPORT_BAND_DATA_ARRAY } from "./graphql";

type RowSubmissionState = "idle" | "loading" | "success" | "error";

const BATCH_SIZE = 10;

export const BandCsvImportContent = () => {
  const [fileName, setFileName] = useState<string>();
  const [rows, setRows] = useState<CsvBandRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [headerPreview, setHeaderPreview] = useState<string[]>([]);
  const [rowWarnings, setRowWarnings] = useState<string[]>([]);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [rawRowCount, setRawRowCount] = useState<number>(0);
  const [rowStatuses, setRowStatuses] = useState<
    Record<number, RowSubmissionState>
  >({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionCounts, setSubmissionCounts] = useState<{
    success: number;
    failure: number;
  } | null>(null);

  const organisationId = process.env.REACT_APP_ORG_ID ?? "";
  const [importBandDataArrayMutation] = useMutation(IMPORT_BAND_DATA_ARRAY);

  const payload = useMemo(
    () => buildBandImportPayload(rows, organisationId),
    [rows, organisationId]
  );

  useEffect(() => {
    setRowStatuses({});
    setRowErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(false);
  }, [rows]);

  const handleReset = () => {
    setRows([]);
    setFileName(undefined);
    setParseError(null);
    setHeaderPreview([]);
    setRowWarnings([]);
    setCopyMessage(null);
    setRawRowCount(0);
    setRowStatuses({});
    setRowErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(false);
  };

  const handleCopyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyMessage("Payload copied to clipboard.");
    } catch {
      setCopyMessage("Unable to access the clipboard API in this browser.");
    }
  };

  const handleSubmitRows = async () => {
    if (rows.length === 0 || isSubmitting) {
      return;
    }

    setRowStatuses({});
    setRowErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(true);

    let successCount = 0;
    let failureCount = 0;

    try {
      for (let startIndex = 0; startIndex < rows.length; startIndex += BATCH_SIZE) {
        const batchRows = rows.slice(startIndex, startIndex + BATCH_SIZE);
        const batchIndices = batchRows.map((_, offset) => startIndex + offset);

        setRowStatuses((prev) => {
          const next = { ...prev };
          batchIndices.forEach((batchIndex) => {
            next[batchIndex] = "loading";
          });
          return next;
        });

        const batchPayload = buildBandImportPayload(batchRows, organisationId);

        try {
          await importBandDataArrayMutation({
            variables: {
              bands: batchPayload,
            },
          });

          successCount += batchRows.length;

          setRowStatuses((prev) => {
            const next = { ...prev };
            batchIndices.forEach((batchIndex) => {
              next[batchIndex] = "success";
            });
            return next;
          });

          setRowErrors((prev) => {
            const next = { ...prev };
            batchIndices.forEach((batchIndex) => {
              if (batchIndex in next) {
                delete next[batchIndex];
              }
            });
            return next;
          });
        } catch (error) {
          failureCount += batchRows.length;
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";

          setRowStatuses((prev) => {
            const next = { ...prev };
            batchIndices.forEach((batchIndex) => {
              next[batchIndex] = "error";
            });
            return next;
          });

          setRowErrors((prev) => {
            const next = { ...prev };
            batchIndices.forEach((batchIndex) => {
              next[batchIndex] = errorMessage;
            });
            return next;
          });
        }
      }
    } finally {
      setSubmissionCounts({ success: successCount, failure: failureCount });
      setIsSubmitting(false);

      if (rows.length > 0) {
        if (failureCount === 0) {
          toast.success(`Submitted ${successCount} row(s) successfully.`);
        } else {
          toast.error(
            `Submitted ${successCount + failureCount} row(s): ${failureCount} failed.`
          );
        }
      }
    }
  };

  const renderStatusIndicator = (
    status?: RowSubmissionState,
    errorMessage?: string
  ) => {
    if (status === "loading") {
      return <CircularProgress size={18} />;
    }

    if (status === "success") {
      return <CheckCircleOutlineIcon color="success" fontSize="small" />;
    }

    if (status === "error") {
      const icon = <HighlightOffIcon color="error" fontSize="small" />;

      return errorMessage ? <Tooltip title={errorMessage}>{icon}</Tooltip> : icon;
    }

    return null;
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setFileName(file.name);
    setParseError(null);
    setRowWarnings([]);
    setCopyMessage(null);
    setRows([]);
    setHeaderPreview([]);
    setRawRowCount(0);

    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result: ParseResult<string[]>) => {
        const { data, errors } = result;

        if (errors.length > 0) {
          const firstError: ParseError = errors[0];
          setParseError(
            `Unable to parse CSV (Row ${firstError.row}): ${firstError.message}`
          );
          return;
        }

        const tableRows = data.filter((row: any) => Array.isArray(row)) as string[][];

        const { headerRow, rows: parsedRows, warnings, rawRowCount, error } =
          parseBandCsvTableRows(tableRows);

        setHeaderPreview(headerRow);

        if (error) {
          setParseError(error);
          return;
        }

        setRawRowCount(rawRowCount);
        setRows(parsedRows);
        setRowWarnings(warnings);
      },
      error: (error: any) => {
        setParseError(`Failed to read the CSV file: ${error.message}`);
      },
    });

    event.target.value = "";
  };

  const previewRows = rows;

  return (
      <Stack spacing={3}>
        <Typography variant="body1">
          Select a CSV file using case-insensitive headers in any order. Required
          headers: Title/Name, Excerpt/Description, Permalink/Source,
          URL/imageUrl. Optional: ID/External Id.
          {" "}
          Extra columns will be ignored.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button variant="contained" component="label">
            Choose CSV
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRows}
            disabled={rows.length === 0 || isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress color="inherit" size={16} />
              ) : undefined
            }
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
          {rows.length > 0 || parseError ? (
            <Button variant="outlined" onClick={handleReset}>
              Clear
            </Button>
          ) : null}
        </Box>
        {fileName && (
          <Typography variant="body2" color="text.secondary">
            Selected file:
            {" "}
            {fileName}
          </Typography>
        )}
        {parseError && (
          <Alert severity="error">
            {parseError}
          </Alert>
        )}
        {rowWarnings.length > 0 && !parseError && (
          <Alert severity="warning">
            <Stack spacing={0.5}>
              {rowWarnings.map((warning, index) => (
                <Typography key={`${warning}-${index}`} variant="body2">
                  {warning}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        {headerPreview.length > 0 && !parseError && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2">Detected header</Typography>
              <Typography variant="body2" color="text.secondary">
                {headerPreview.join(", ")}
              </Typography>
            </CardContent>
          </Card>
        )}
        {rows.length > 0 && (
          <>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2">Parsed rows</Typography>
                <Typography variant="body2" color="text.secondary">
                  {rows.length}
                  {" "}
                  row(s) ready for submission
                  {rawRowCount !== rows.length
                    ? ` (${rawRowCount - rows.length} empty rows skipped)`
                    : ""}
                  .
                </Typography>
              </CardContent>
            </Card>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {PREVIEW_HEADERS.map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, index) => (
                    <TableRow key={`${row.id}-${index}`}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.excerpt}</TableCell>
                      <TableCell>{row.permalink}</TableCell>
                      <TableCell>{row.url}</TableCell>
                      <TableCell align="center">
                        {renderStatusIndicator(
                          rowStatuses[index],
                          rowErrors[index]
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    API payload preview
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      onClick={handleCopyPayload}
                      disabled={payload.length === 0}
                    >
                      Copy JSON
                    </Button>
                  </Stack>
                </Box>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#0b0b0b",
                    color: "#f1f1f1",
                    borderRadius: 1,
                    padding: 2,
                    maxHeight: 320,
                    overflow: "auto",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                  }}
                >
                  {JSON.stringify(payload, null, 2)}
                </Box>
                {copyMessage && (
                  <Typography variant="caption" color="text.secondary">
                    {copyMessage}
                  </Typography>
                )}
              </CardContent>
            </Card>
            {submissionCounts && (
              <Alert
                severity={submissionCounts.failure > 0 ? "warning" : "success"}
              >
                Submitted
                {" "}
                {rows.length}
                {" "}
                row(s). Success:
                {" "}
                {submissionCounts.success}
                {" "}
                Failure:
                {" "}
                {submissionCounts.failure}
                .
              </Alert>
            )}
          </>
        )}
      </Stack>
  );
};

const CsvImportPage = () => {
  return (
    <PageContainer title="Band CSV Import">
      <BandCsvImportContent />
    </PageContainer>
  );
};

export default CsvImportPage;
